/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef } from 'react';
import { 
  FileCheck2, 
  Folder, 
  FolderOpen, 
  UploadCloud, 
  Download, 
  Plus, 
  Search, 
  CheckCircle2, 
  Clock, 
  Users, 
  FileText, 
  Trash2, 
  Edit3, 
  ArrowLeft, 
  Paperclip, 
  FileSpreadsheet, 
  Bell, 
  ChevronRight, 
  AlertOctagon, 
  AlertTriangle,
  Printer,
  Sparkles,
  FileDown
} from 'lucide-react';
import { CostCenter, DC3Record, DC3NotificationSettings } from '../types.ts';
import { generateOfficialDC3PDF, generateDC3FolderSummaryPDF } from '../utils/safetyPdfGenerator.ts';
import { DC3NotificationConfigModal } from './DC3NotificationConfigModal.tsx';
import { DC3ExcelExportModal } from './DC3ExcelExportModal.tsx';
import { exportDC3RecordsToExcel } from '../utils/dc3ExcelExporter.ts';

interface DC3ManagerProps {
  activeCostCenter: CostCenter | null;
  dc3Records: DC3Record[];
  onSaveDC3: (records: DC3Record[]) => void;
  onDeleteDC3: (id: string) => void;
}

export const DC3Manager: React.FC<DC3ManagerProps> = ({
  activeCostCenter,
  dc3Records,
  onSaveDC3,
  onDeleteDC3
}) => {
  // Navigation & View States
  const [viewMode, setViewMode] = useState<'folders' | 'table'>('folders');
  const [selectedFolderEmployee, setSelectedFolderEmployee] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'vigente' | 'por_renovar' | 'vencido'>('all');

  // Modals
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isSingleModalOpen, setIsSingleModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNotifConfigOpen, setIsNotifConfigOpen] = useState(false);
  const [isExcelExportModalOpen, setIsExcelExportModalOpen] = useState(false);
  const [excelFilterPreset, setExcelFilterPreset] = useState<'all' | 'expiring_30' | 'expired' | 'expiring_and_expired' | 'valid'>('expiring_30');

  // Form State
  const [formRecord, setFormRecord] = useState<Partial<DC3Record>>({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Bulk Upload State
  const [bulkText, setBulkText] = useState('');
  const [bulkParsedRecords, setBulkParsedRecords] = useState<DC3Record[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate Expiry Status based on 1-year validity (365 days) and 1 month (30 days) alert
  const calculateValidity = (issueDateStr: string, customExpiry?: string): {
    expiryDate: string;
    daysRemaining: number;
    computedStatus: 'vigente' | 'por_renovar' | 'vencido';
  } => {
    let expiryDateStr = customExpiry;
    if (!expiryDateStr && issueDateStr) {
      const issue = new Date(issueDateStr);
      if (!isNaN(issue.getTime())) {
        const nextYear = new Date(issue);
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        expiryDateStr = nextYear.toISOString().split('T')[0];
      }
    }

    if (!expiryDateStr) {
      const today = new Date();
      today.setFullYear(today.getFullYear() + 1);
      expiryDateStr = today.toISOString().split('T')[0];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDateStr);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry.getTime() - today.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let computedStatus: 'vigente' | 'por_renovar' | 'vencido' = 'vigente';
    if (daysRemaining < 0) {
      computedStatus = 'vencido';
    } else if (daysRemaining <= 30) {
      // 1 month before expiry alert
      computedStatus = 'por_renovar';
    } else {
      computedStatus = 'vigente';
    }

    return { expiryDate: expiryDateStr, daysRemaining, computedStatus };
  };

  // Enriched records with real-time validity
  const enrichedRecords = useMemo(() => {
    return dc3Records.map(r => {
      const { expiryDate, daysRemaining, computedStatus } = calculateValidity(r.issueDate, r.expiryDate);
      return {
        ...r,
        expiryDate,
        status: computedStatus,
        daysRemaining
      };
    });
  }, [dc3Records]);

  // Expiration notifications (< 30 days or expired)
  const expiringSoonRecords = useMemo(() => {
    return enrichedRecords.filter(r => r.status === 'por_renovar');
  }, [enrichedRecords]);

  const expiredRecords = useMemo(() => {
    return enrichedRecords.filter(r => r.status === 'vencido');
  }, [enrichedRecords]);

  const activeAlertsCount = expiringSoonRecords.length + expiredRecords.length;

  // Group records by Employee Name (Folders)
  const employeeFolders = useMemo(() => {
    const map = new Map<string, DC3Record[]>();
    enrichedRecords.forEach(rec => {
      const name = rec.employeeName?.trim() || 'Sin Asignar';
      if (!map.has(name)) {
        map.set(name, []);
      }
      map.get(name)!.push(rec);
    });

    const folders = Array.from(map.entries()).map(([employeeName, records]) => {
      records.sort((a, b) => (b.issueDate || '').localeCompare(a.issueDate || ''));

      const hasExpired = records.some(r => r.status === 'vencido');
      const hasExpiringSoon = records.some(r => r.status === 'por_renovar');
      let folderStatus: 'vigente' | 'por_renovar' | 'vencido' = 'vigente';
      if (hasExpired) folderStatus = 'vencido';
      else if (hasExpiringSoon) folderStatus = 'por_renovar';

      const totalHours = records.reduce((acc, r) => acc + (Number(r.durationHours) || 0), 0);
      const latestRecord = records[0];
      const curp = records.find(r => r.curp)?.curp || '';
      const employeeId = records.find(r => r.employeeId)?.employeeId || '';
      const role = records.find(r => r.role)?.role || 'Colaborador';

      return {
        employeeName,
        records,
        count: records.length,
        totalHours,
        folderStatus,
        latestRecord,
        curp,
        employeeId,
        role
      };
    });

    return folders.sort((a, b) => {
      const order = { vencido: 0, por_renovar: 1, vigente: 2 };
      if (order[a.folderStatus] !== order[b.folderStatus]) {
        return order[a.folderStatus] - order[b.folderStatus];
      }
      return a.employeeName.localeCompare(b.employeeName);
    });
  }, [enrichedRecords]);

  // Filtered Folders
  const filteredFolders = useMemo(() => {
    return employeeFolders.filter(folder => {
      const matchSearch = 
        folder.employeeName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        folder.curp.toLowerCase().includes(searchFilter.toLowerCase()) ||
        folder.employeeId.toLowerCase().includes(searchFilter.toLowerCase()) ||
        folder.role.toLowerCase().includes(searchFilter.toLowerCase()) ||
        folder.records.some(r => r.courseName.toLowerCase().includes(searchFilter.toLowerCase()));

      if (!matchSearch) return false;

      if (statusFilter === 'all') return true;
      return folder.folderStatus === statusFilter;
    });
  }, [employeeFolders, searchFilter, statusFilter]);

  // Filtered Records (Table Mode)
  const filteredRecords = useMemo(() => {
    return enrichedRecords.filter(rec => {
      const matchSearch = 
        rec.employeeName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (rec.curp && rec.curp.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (rec.employeeId && rec.employeeId.toLowerCase().includes(searchFilter.toLowerCase())) ||
        rec.courseName.toLowerCase().includes(searchFilter.toLowerCase()) ||
        rec.trainer.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (rec.stpsReg && rec.stpsReg.toLowerCase().includes(searchFilter.toLowerCase()));

      if (!matchSearch) return false;

      if (statusFilter === 'all') return true;
      return rec.status === statusFilter;
    });
  }, [enrichedRecords, searchFilter, statusFilter]);

  // Active folder details
  const activeFolder = useMemo(() => {
    if (!selectedFolderEmployee) return null;
    return employeeFolders.find(f => f.employeeName === selectedFolderEmployee) || null;
  }, [employeeFolders, selectedFolderEmployee]);

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const headers = [
      'Nombre del Colaborador',
      'CURP',
      'No. Nomina / ID',
      'Puesto u Ocupacion',
      'Clave Ocupacion STPS',
      'Nombre del Curso / Norma',
      'Area Tematica STPS',
      'Agente Capacitador / Instructor',
      'Registro STPS Capacitador',
      'Duracion Horas',
      'Fecha Emision (AAAA-MM-DD)',
      'Fecha Inicio (AAAA-MM-DD)',
      'Fecha Termino (AAAA-MM-DD)',
      'Calificacion / Evaluacion'
    ];

    const sampleRows = [
      [
        'Juan Carlos Mendoza Reyes',
        'MERJ850412HDFNR03',
        'ISN-OP-101',
        'Operador de Hornos Térmicos',
        '01.1',
        'NOM-017-STPS-2008 Equipo de Protección Personal y Seguridad Operativa',
        '6000 - Seguridad en el Trabajo',
        'Ing. Alondra Tovar (Supervisor HSEQ)',
        'STPS-AUT-2026-ISN09',
        '16',
        '2026-04-15',
        '2026-04-14',
        '2026-04-15',
        '100%'
      ],
      [
        'María Fernanda López Gómez',
        'LOGM920918MDFNR08',
        'ISN-OP-102',
        'Técnico Químico en Decapado',
        '02.4',
        'NOM-018-STPS-2015 Sistema Armonizado para Manejo de Sustancias Químicas',
        '6000 - Seguridad en el Trabajo',
        'Ing. Fernando Guzmán (Seguridad Industrial)',
        'STPS-AUT-2026-ISN09',
        '20',
        '2026-03-20',
        '2026-03-18',
        '2026-03-20',
        '95%'
      ],
      [
        'Roberto Silva Hernández',
        'SIHR881105HGTNR01',
        'ISN-OP-103',
        'Mecánico Industrial / Mantenimiento',
        '03.2',
        'NOM-009-STPS-2011 Trabajo Seguro en Alturas y Uso de Arnés',
        '6000 - Seguridad en el Trabajo',
        'Centro de Capacitación Técnica STPS',
        'STPS-CCT-8891-2026',
        '16',
        '2025-09-10',
        '2025-09-08',
        '2025-09-10',
        '100%'
      ]
    ];

    let csvContent = '\uFEFF'; // BOM for Excel UTF-8
    csvContent += headers.join(',') + '\n';
    sampleRows.forEach(row => {
      csvContent += row.map(val => `"${val.replace(/"/g, '""')}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'Plantilla_Carga_Masiva_Formatos_DC3_STPS.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Parse Bulk Content
  const parseBulkContent = (text: string) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) {
      setBulkErrors(['El archivo o texto está vacío.']);
      setBulkParsedRecords([]);
      return;
    }

    const errors: string[] = [];
    const parsed: DC3Record[] = [];

    let startIdx = 0;
    const firstLineLower = lines[0].toLowerCase();
    if (firstLineLower.includes('nombre') || firstLineLower.includes('curp') || firstLineLower.includes('curso')) {
      startIdx = 1;
    }

    for (let i = startIdx; i < lines.length; i++) {
      const line = lines[i];
      const delimiter = line.includes('\t') ? '\t' : ',';
      let cols: string[] = [];
      
      if (delimiter === '\t') {
        cols = line.split('\t').map(c => c.trim().replace(/^["']|["']$/g, ''));
      } else {
        const regex = /(?:^|,)(?:"([^"]*(?:""[^"]*)*)"|([^,]*))/g;
        let match;
        cols = [];
        while ((match = regex.exec(line)) !== null) {
          let val = match[1] !== undefined ? match[1].replace(/""/g, '"') : match[2];
          if (val === undefined) val = '';
          cols.push(val.trim());
          if (regex.lastIndex >= line.length) break;
        }
      }

      if (cols.length < 2) continue;

      const employeeName = cols[0] || '';
      const curp = cols[1] || '';
      const employeeId = cols[2] || `ISN-${Math.floor(100 + Math.random() * 900)}`;
      const role = cols[3] || 'Operador';
      const occupationCode = cols[4] || '01.1';
      const courseName = cols[5] || cols[2] || '';
      const thematicArea = cols[6] || '6000 - Seguridad en el Trabajo';
      const trainer = cols[7] || 'Instructor de Seguridad HSEQ';
      const stpsReg = cols[8] || 'STPS-REG-2026';
      const durationHours = parseInt(cols[9] || '16', 10) || 16;
      const issueDate = cols[10] || new Date().toISOString().split('T')[0];
      const startDate = cols[11] || issueDate;
      const endDate = cols[12] || issueDate;
      const score = cols[13] || '100%';

      if (!employeeName || !courseName) {
        errors.push(`Fila ${i + 1}: Falta el Nombre del Colaborador o Nombre del Curso.`);
        continue;
      }

      const { expiryDate, computedStatus } = calculateValidity(issueDate);

      const record: DC3Record = {
        id: `DC3-${Date.now().toString().slice(-4)}${i}`,
        costCenterId: activeCostCenter?.id || 'CC-BAJIO',
        costCenterName: activeCostCenter?.name || 'Sede Bajío',
        employeeName,
        curp,
        employeeId,
        role,
        occupationCode,
        courseName,
        thematicArea,
        trainer,
        stpsReg,
        durationHours,
        issueDate,
        startDate,
        endDate,
        expiryDate,
        status: computedStatus,
        score,
        createdAt: new Date().toISOString()
      };

      parsed.push(record);
    }

    setBulkErrors(errors);
    setBulkParsedRecords(parsed);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setBulkText(content);
      parseBulkContent(content);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setBulkText(content);
        parseBulkContent(content);
      };
      reader.readAsText(file, 'UTF-8');
    }
  };

  const handleConfirmBulkUpload = () => {
    if (bulkParsedRecords.length === 0) return;
    const updated = [...bulkParsedRecords, ...dc3Records];
    onSaveDC3(updated);
    setIsBulkModalOpen(false);
    setBulkText('');
    setBulkParsedRecords([]);
    setBulkErrors([]);
  };

  // Single Record Handlers
  const handleOpenNewSingle = (prefillEmployee?: string) => {
    const today = new Date().toISOString().split('T')[0];
    const { expiryDate } = calculateValidity(today);

    setFormRecord({
      id: `DC3-${Date.now().toString().slice(-5)}`,
      costCenterId: activeCostCenter?.id || 'CC-BAJIO',
      costCenterName: activeCostCenter?.name || 'Sede Bajío',
      employeeName: prefillEmployee || '',
      employeeId: `ISN-${Math.floor(100 + Math.random() * 900)}`,
      curp: '',
      role: 'Operador de Producción',
      occupationCode: '01.1 - Operación Industrial',
      courseName: 'NOM-017-STPS-2008 Equipo de Protección Personal',
      thematicArea: '6000 - Seguridad en el Trabajo',
      trainer: 'Ing. Alondra Tovar (Supervisor HSEQ)',
      stpsReg: 'STPS-AUT-2026-ISN09',
      durationHours: 16,
      startDate: today,
      endDate: today,
      issueDate: today,
      expiryDate: expiryDate,
      score: '100%',
      legalRepresentativeName: 'Ing. Fernando Guzmán V.',
      workerRepresentativeName: 'Comisión Mixta NOM-019'
    });
    setIsSingleModalOpen(true);
  };

  const handleOpenEdit = (rec: DC3Record) => {
    setFormRecord({ ...rec });
    setIsEditModalOpen(true);
  };

  const handleSaveForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRecord.employeeName || !formRecord.courseName) return;

    const issueDate = formRecord.issueDate || new Date().toISOString().split('T')[0];
    const { expiryDate, computedStatus } = calculateValidity(issueDate, formRecord.expiryDate);

    const recordToSave: DC3Record = {
      id: formRecord.id || `DC3-${Date.now().toString().slice(-5)}`,
      costCenterId: activeCostCenter?.id || formRecord.costCenterId || 'CC-BAJIO',
      costCenterName: activeCostCenter?.name || formRecord.costCenterName || 'Sede Bajío',
      employeeName: formRecord.employeeName.trim(),
      employeeId: formRecord.employeeId || 'ISN-001',
      curp: formRecord.curp || '',
      role: formRecord.role || 'Operador',
      occupationCode: formRecord.occupationCode || '01.1',
      courseName: formRecord.courseName.trim(),
      thematicArea: formRecord.thematicArea || '6000 - Seguridad en el Trabajo',
      trainer: formRecord.trainer || 'Supervisor HSEQ',
      stpsReg: formRecord.stpsReg || 'STPS-REG-2026',
      durationHours: Number(formRecord.durationHours) || 16,
      startDate: formRecord.startDate || issueDate,
      endDate: formRecord.endDate || issueDate,
      issueDate: issueDate,
      expiryDate: expiryDate,
      status: computedStatus,
      score: formRecord.score || '100%',
      scannedDocumentUrl: formRecord.scannedDocumentUrl,
      scannedDocumentName: formRecord.scannedDocumentName,
      legalRepresentativeName: formRecord.legalRepresentativeName || 'Ing. Fernando Guzmán V.',
      workerRepresentativeName: formRecord.workerRepresentativeName || 'Comisión Mixta NOM-019',
      notes: formRecord.notes || '',
      updatedAt: new Date().toISOString()
    };

    const existingIdx = dc3Records.findIndex(r => r.id === recordToSave.id);
    let updatedList: DC3Record[];
    if (existingIdx >= 0) {
      updatedList = [...dc3Records];
      updatedList[existingIdx] = recordToSave;
    } else {
      updatedList = [recordToSave, ...dc3Records];
    }

    onSaveDC3(updatedList);
    setIsSingleModalOpen(false);
    setIsEditModalOpen(false);
    setFormRecord({});
  };

  // PDF Generation Handlers
  const handleDownloadSingleDC3PDF = async (rec: DC3Record) => {
    setIsGeneratingPdf(true);
    try {
      await generateOfficialDC3PDF(rec, {
        name: 'SERVICIOS NUEVOS DE FILTRACIÓN S.A. DE C.V.',
        rfc: 'SNF120418-XYZ',
        location: activeCostCenter ? `${activeCostCenter.name} - Planta Operativa` : 'Planta Operativa Bajío'
      });
    } catch (err) {
      console.error('Error generating DC-3 PDF', err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadFolderSummary = async (folderName: string, records: DC3Record[]) => {
    setIsGeneratingPdf(true);
    try {
      await generateDC3FolderSummaryPDF(folderName, records, activeCostCenter?.name);
    } catch (err) {
      console.error('Error generating Folder PDF', err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Scanned doc attachment handler
  const handleAttachScannedDoc = (e: React.ChangeEvent<HTMLInputElement>, recordId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      const updated = dc3Records.map(r => {
        if (r.id === recordId) {
          return {
            ...r,
            scannedDocumentUrl: dataUrl,
            scannedDocumentName: file.name
          };
        }
        return r;
      });
      onSaveDC3(updated);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-[#121317] border border-[#262836] rounded-2xl p-4 sm:p-6 space-y-6 text-white">
      
      {/* 1. TOP HEADER & COMPLIANCE BADGE */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-[#222430] pb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950/80 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
              <FileCheck2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                Padrón Oficial de Formatos DC-3 (STPS / SIRCE)
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-500/40">
                  Vigencia 1 Año
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Constancias de competencias laborales organizadas en carpetas digitales por colaborador con alertas de renovación mensual.
              </p>
            </div>
          </div>
        </div>

        {/* Global Actions */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={() => {
              setExcelFilterPreset('expiring_30');
              setIsExcelExportModalOpen(true);
            }}
            className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-950/80 via-teal-900/40 to-emerald-950/80 hover:from-emerald-900/90 hover:to-teal-800 text-emerald-300 text-xs font-bold flex items-center gap-2 border border-emerald-500/50 hover:border-emerald-400 shadow-md shadow-emerald-950/40 transition-all cursor-pointer group"
            title="Exportar constancias DC-3 a Excel profesional con logo, filtros de vencimiento a 30 días, métricas y firmas STPS"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-400 group-hover:scale-110 transition-transform" />
            <span>Exportar a Excel (Filtros Pro & Logo)</span>
          </button>

          <button
            onClick={() => setIsNotifConfigOpen(true)}
            className="px-3.5 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-bold flex items-center gap-2 border border-amber-500/30 transition-colors cursor-pointer"
            title="Configurar frecuencias de notificación, canales push y ventanas de anticipación"
          >
            <Bell className="w-4 h-4 text-amber-400" />
            <span>Alertas & Frecuencias Push</span>
          </button>

          <button
            onClick={handleDownloadTemplate}
            className="px-3.5 py-2 rounded-xl bg-[#1e2029] hover:bg-slate-800 text-slate-200 text-xs font-semibold flex items-center gap-2 border border-[#2e3140] transition-colors cursor-pointer"
            title="Descargar plantilla CSV con ejemplos de normas STPS"
          >
            <FileDown className="w-4 h-4 text-slate-400" />
            <span>Plantilla STPS</span>
          </button>

          <button
            onClick={() => setIsBulkModalOpen(true)}
            className="px-3.5 py-2 rounded-xl bg-[#262938] hover:bg-slate-700 text-slate-100 text-xs font-bold flex items-center gap-2 border border-slate-600 transition-colors cursor-pointer"
          >
            <UploadCloud className="w-4 h-4 text-sky-400" />
            <span>Carga Masiva (CSV / Excel)</span>
          </button>

          <button
            onClick={() => handleOpenNewSingle()}
            className="px-4 py-2 rounded-xl bg-[#e52e2e] hover:bg-[#ff3d3d] text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-red-950/40 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Nueva Constancia DC-3</span>
          </button>
        </div>
      </div>

      {/* 2. REGULATORY KPI CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#181920] border border-[#282a36] rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Formatos DC-3</span>
            <FileCheck2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-xl font-bold text-white">{enrichedRecords.length}</div>
          <p className="text-[10px] text-slate-400">En {employeeFolders.length} expedientes digitales</p>
        </div>

        <div className="bg-[#181920] border border-[#282a36] rounded-xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Vigentes (100%)</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-xl font-bold text-emerald-400">
            {enrichedRecords.filter(r => r.status === 'vigente').length}
          </div>
          <p className="text-[10px] text-emerald-500 font-medium">Con más de 30 días de vigencia</p>
        </div>

        <div className="bg-[#181920] border border-amber-500/30 rounded-xl p-3.5 space-y-1 bg-amber-950/10 relative group">
          <div className="flex items-center justify-between text-amber-300">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Próximos a Vencer</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExcelFilterPreset('expiring_30');
                setIsExcelExportModalOpen(true);
              }}
              className="text-amber-400 hover:text-amber-200 p-1 hover:bg-amber-900/40 rounded transition-colors cursor-pointer"
              title="Descargar reporte Excel de formatos próximos a vencer (30 días)"
            >
              <FileSpreadsheet className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xl font-bold text-amber-400">
            {expiringSoonRecords.length}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-amber-300">Alerta: Vencen en &lt; 30 días</p>
            <button
              onClick={() => {
                setExcelFilterPreset('expiring_30');
                setIsExcelExportModalOpen(true);
              }}
              className="text-[10px] text-amber-300 font-bold underline hover:text-amber-100 cursor-pointer"
            >
              Excel 30d
            </button>
          </div>
        </div>

        <div className="bg-[#181920] border border-red-500/30 rounded-xl p-3.5 space-y-1 bg-red-950/10">
          <div className="flex items-center justify-between text-red-300">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Vencidos (+1 Año)</span>
            <AlertOctagon className="w-4 h-4 text-red-400" />
          </div>
          <div className="text-xl font-bold text-red-400">
            {expiredRecords.length}
          </div>
          <p className="text-[10px] text-red-400">Requieren renovación urgente</p>
        </div>
      </div>

      {/* 3. ALERT / NOTIFICATION BANNER (1 MONTH BEFORE EXPIRY) */}
      {activeAlertsCount > 0 && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-amber-950/60 via-amber-900/30 to-red-950/40 border border-amber-500/40 space-y-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-amber-300 font-bold text-xs sm:text-sm">
              <Bell className="w-4 h-4 text-amber-400 animate-bounce" />
              <span>Notificación de Vencimientos de Formatos DC-3 (Normativa STPS):</span>
            </div>
            <span className="px-2 py-0.5 rounded-full bg-amber-950 text-amber-300 border border-amber-500 text-[10px] font-mono font-bold">
              {activeAlertsCount} Alertas Activas
            </span>
          </div>
          
          <p className="text-xs text-slate-300 leading-relaxed">
            Se han detectado <strong className="text-amber-300">{expiringSoonRecords.length} constancias DC-3 próximas a cumplir 1 año</strong> (vencimiento en menos de 30 días) y <strong className="text-red-300">{expiredRecords.length} constancias vencidas</strong>. Conforme al programa anual de capacitación STPS, se sugiere programar la renovación de cursos para mantener el 100% de cumplimiento en auditorías.
          </p>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={() => {
                setStatusFilter('por_renovar');
                setSelectedFolderEmployee(null);
              }}
              className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Ver Próximos a Vencer ({expiringSoonRecords.length})</span>
            </button>

            {/* Quick Excel Export 30-Day Expiry button */}
            <button
              onClick={() => {
                setExcelFilterPreset('expiring_30');
                setIsExcelExportModalOpen(true);
              }}
              className="px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-amber-950 via-emerald-950 to-amber-950 hover:from-amber-900 hover:to-emerald-900 text-amber-200 border border-amber-500/50 hover:border-amber-400 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-md"
              title="Descargar reporte Excel con logo y formato profesional de cursos que vencen en 30 días"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-amber-400" />
              <span>Descargar Próximos a Vencer (&lt; 30d) en Excel</span>
            </button>

            {expiredRecords.length > 0 && (
              <button
                onClick={() => {
                  setStatusFilter('vencido');
                  setSelectedFolderEmployee(null);
                }}
                className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Ver Vencidos ({expiredRecords.length})</span>
              </button>
            )}

            {statusFilter !== 'all' && (
              <button
                onClick={() => setStatusFilter('all')}
                className="px-3 py-1 rounded-lg bg-[#222430] hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors cursor-pointer"
              >
                Mostrar Todos
              </button>
            )}
          </div>
        </div>
      )}

      {/* 4. CONTROLS BAR: SEARCH, FILTERS & VIEW MODE */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#181920] p-3 rounded-xl border border-[#282a36]">
        
        {/* Left: Search & Filter */}
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por colaborador, CURP, puesto, curso..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full bg-[#121317] border border-[#2e3140] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div className="flex items-center gap-1.5 bg-[#121317] p-1 rounded-xl border border-[#2e3140]">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === 'all' ? 'bg-[#222430] text-white shadow' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Todos ({enrichedRecords.length})
            </button>
            <button
              onClick={() => setStatusFilter('vigente')}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === 'vigente' ? 'bg-emerald-950 text-emerald-300 border border-emerald-600/40' : 'text-slate-400 hover:text-emerald-400'
              }`}
            >
              Vigentes
            </button>
            <button
              onClick={() => setStatusFilter('por_renovar')}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === 'por_renovar' ? 'bg-amber-950 text-amber-300 border border-amber-600/40' : 'text-slate-400 hover:text-amber-400'
              }`}
            >
              Por Vencer (&lt; 30d)
            </button>
            <button
              onClick={() => setStatusFilter('vencido')}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === 'vencido' ? 'bg-red-950 text-red-300 border border-red-600/40' : 'text-slate-400 hover:text-red-400'
              }`}
            >
              Vencidos
            </button>
          </div>
        </div>

        {/* Right: View Toggle & Excel Quick Action */}
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => {
              if (statusFilter === 'por_renovar') {
                setExcelFilterPreset('expiring_30');
              } else if (statusFilter === 'vencido') {
                setExcelFilterPreset('expired');
              } else if (statusFilter === 'vigente') {
                setExcelFilterPreset('valid');
              } else {
                setExcelFilterPreset('all');
              }
              setIsExcelExportModalOpen(true);
            }}
            className="px-3 py-1.5 rounded-xl bg-[#1e2029] hover:bg-slate-800 text-emerald-400 text-xs font-bold flex items-center gap-1.5 border border-emerald-500/30 transition-colors cursor-pointer"
            title="Exportar vista filtrada a Excel profesional"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Excel</span>
          </button>

          <div className="flex items-center gap-1 bg-[#121317] p-1 rounded-xl border border-[#2e3140]">
            <button
              onClick={() => {
                setViewMode('folders');
                setSelectedFolderEmployee(null);
              }}
              className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                viewMode === 'folders' ? 'bg-emerald-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Folder className="w-3.5 h-3.5" />
              <span>Carpetas ({employeeFolders.length})</span>
            </button>
            <button
              onClick={() => {
                setViewMode('table');
                setSelectedFolderEmployee(null);
              }}
              className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                viewMode === 'table' ? 'bg-emerald-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Vista Tabla ({enrichedRecords.length})</span>
            </button>
          </div>
        </div>
      </div>

      {/* 5. MAIN CONTENT AREA */}
      {viewMode === 'folders' ? (
        // VISTA POR CARPETAS DE COLABORADORES
        <div>
          {selectedFolderEmployee && activeFolder ? (
            // DETALLE DE EXPEDIENTE DEL COLABORADOR
            <div className="space-y-5">
              {/* Folder Breadcrumb & Header */}
              <div className="p-4 rounded-xl bg-[#181920] border border-[#282a36] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedFolderEmployee(null)}
                    className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-semibold mb-1 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Volver a Todas las Carpetas</span>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-800 flex items-center justify-center text-white font-bold text-lg shadow-md">
                      {activeFolder.employeeName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                        {activeFolder.employeeName}
                        {activeFolder.folderStatus === 'vigente' && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-950/80 text-emerald-300 border border-emerald-600 text-[10px] font-bold">
                            Expediente 100% Vigente
                          </span>
                        )}
                        {activeFolder.folderStatus === 'por_renovar' && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-950/80 text-amber-300 border border-amber-600 text-[10px] font-bold">
                            Próximo a Vencer (&lt; 30d)
                          </span>
                        )}
                        {activeFolder.folderStatus === 'vencido' && (
                          <span className="px-2 py-0.5 rounded-full bg-red-950/80 text-red-300 border border-red-600 text-[10px] font-bold">
                            Constancias Vencidas
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-slate-400">
                        Puesto: <strong className="text-slate-200">{activeFolder.role}</strong> | ID: <strong className="text-slate-200">{activeFolder.employeeId}</strong> | CURP: <strong className="text-slate-200">{activeFolder.curp || 'N/A'}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Folder Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      exportDC3RecordsToExcel(activeFolder.records, {
                        fileName: `Expediente_DC3_${activeFolder.employeeName.replace(/\s+/g, '_')}.xlsx`,
                        reportTitle: `Expediente de Constancias DC-3 - ${activeFolder.employeeName}`,
                        filterDescription: `Colaborador: ${activeFolder.employeeName} | Puesto: ${activeFolder.role} | Nómina: ${activeFolder.employeeId}`,
                        costCenterName: activeCostCenter?.name || 'Centro Operativo',
                        generatedBy: 'Auditoría STPS / HSEQ'
                      });
                    }}
                    className="px-3.5 py-2 rounded-xl bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 text-xs font-semibold flex items-center gap-1.5 border border-emerald-600/40 transition-colors cursor-pointer"
                    title="Descargar matriz en Excel con todas las constancias de este colaborador y su vigencia"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Expediente Excel</span>
                  </button>

                  <button
                    onClick={() => handleDownloadFolderSummary(activeFolder.employeeName, activeFolder.records)}
                    disabled={isGeneratingPdf}
                    className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold flex items-center gap-1.5 border border-slate-700 transition-colors cursor-pointer"
                    title="Generar kárdex oficial en PDF con todas las constancias de este colaborador"
                  >
                    <Printer className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Descargar Kárdex PDF</span>
                  </button>

                  <button
                    onClick={() => handleOpenNewSingle(activeFolder.employeeName)}
                    className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 shadow transition-colors cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Agregar DC-3 a esta Carpeta</span>
                  </button>
                </div>
              </div>

              {/* Records List in Folder */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeFolder.records.map((rec) => (
                  <div
                    key={rec.id}
                    className={`bg-[#16171d] rounded-xl border p-4 space-y-3 transition-all ${
                      rec.status === 'vencido'
                        ? 'border-red-600/40 hover:border-red-500'
                        : rec.status === 'por_renovar'
                        ? 'border-amber-600/40 hover:border-amber-500'
                        : 'border-[#262836] hover:border-emerald-500/40'
                    }`}
                  >
                    {/* Header Card */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5">
                        <span className="font-mono text-[10px] font-bold text-red-400 bg-red-950/40 border border-red-800/60 px-2 py-0.5 rounded">
                          {rec.id}
                        </span>
                        <h4 className="font-bold text-white text-sm leading-snug mt-1">
                          {rec.courseName}
                        </h4>
                        <span className="text-[11px] text-slate-400 block">
                          Área Temática: {rec.thematicArea || '6000 - Seguridad'}
                        </span>
                      </div>

                      {/* Status badge */}
                      {rec.status === 'vigente' && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-600/50 whitespace-nowrap">
                          Vigente ({rec.durationHours}h)
                        </span>
                      )}
                      {rec.status === 'por_renovar' && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-950 text-amber-300 border border-amber-600/60 whitespace-nowrap">
                          Por Vencer ({rec.daysRemaining}d)
                        </span>
                      )}
                      {rec.status === 'vencido' && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-950 text-red-300 border border-red-600/60 whitespace-nowrap">
                          Vencido
                        </span>
                      )}
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-2 gap-2 text-xs bg-[#111216] p-2.5 rounded-lg border border-[#222430]">
                      <div>
                        <span className="text-slate-500 text-[10px] block">Capacitador / Registro STPS</span>
                        <strong className="text-slate-200 text-[11px]">{rec.trainer}</strong>
                        <span className="text-[10px] text-slate-400 block font-mono">{rec.stpsReg}</span>
                      </div>

                      <div>
                        <span className="text-slate-500 text-[10px] block">Emisión & Vigencia (1 Año)</span>
                        <span className="text-slate-300 text-[11px] block">Emisión: {rec.issueDate}</span>
                        <strong className={`text-[11px] block ${rec.status === 'vencido' ? 'text-red-400 font-bold' : rec.status === 'por_renovar' ? 'text-amber-400 font-bold' : 'text-emerald-400 font-bold'}`}>
                          Vence: {rec.expiryDate}
                        </strong>
                      </div>
                    </div>

                    {/* Scanned Document Preview if attached */}
                    {rec.scannedDocumentUrl && (
                      <div className="flex items-center justify-between p-2 rounded bg-emerald-950/20 border border-emerald-600/30 text-xs">
                        <span className="text-emerald-300 flex items-center gap-1.5 text-[11px] truncate">
                          <Paperclip className="w-3.5 h-3.5 shrink-0" />
                          {rec.scannedDocumentName || 'Constancia_Escaneada_Firmada.pdf'}
                        </span>
                        <a
                          href={rec.scannedDocumentUrl}
                          download={rec.scannedDocumentName || `Constancia_${rec.employeeName}.pdf`}
                          className="text-emerald-400 hover:underline text-[10px] font-bold"
                        >
                          Ver Archivo
                        </a>
                      </div>
                    )}

                    {/* Card Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-[#222430]">
                      <div className="flex items-center gap-2">
                        {/* Attach scanned document */}
                        <label
                          className="px-2.5 py-1.5 rounded-lg bg-[#20222a] hover:bg-slate-700 text-slate-300 text-xs font-medium flex items-center gap-1.5 cursor-pointer border border-[#2e3140] transition-colors"
                          title="Adjuntar constancia física firmada / escaneada"
                        >
                          <Paperclip className="w-3 h-3 text-sky-400" />
                          <span>{rec.scannedDocumentUrl ? 'Reemplazar Escaneo' : 'Adjuntar Escaneo'}</span>
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            className="hidden"
                            onChange={(e) => handleAttachScannedDoc(e, rec.id)}
                          />
                        </label>

                        {/* Official PDF Generator */}
                        <button
                          onClick={() => handleDownloadSingleDC3PDF(rec)}
                          disabled={isGeneratingPdf}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 border border-emerald-600/40 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                          title="Descargar Formato DC-3 oficial STPS en PDF"
                        >
                          <Download className="w-3 h-3 text-emerald-400" />
                          <span>Descargar DC-3 (STPS)</span>
                        </button>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleOpenEdit(rec)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#20222a] transition-colors cursor-pointer"
                          title="Editar constancia"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => onDeleteDC3(rec.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-950/30 transition-colors cursor-pointer"
                          title="Eliminar registro"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // LISTA DE CARPETAS DE COLABORADORES
            <div className="space-y-4">
              {filteredFolders.length === 0 ? (
                <div className="text-center py-14 px-4 rounded-xl bg-[#181920] border border-[#222430] space-y-3">
                  <FolderOpen className="w-12 h-12 text-slate-600 mx-auto" />
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-white">No se encontraron carpetas de colaboradores</h4>
                    <p className="text-xs text-slate-400 max-w-md mx-auto">
                      {searchFilter || statusFilter !== 'all'
                        ? 'No hay colaboradores que coincidan con los filtros aplicados.'
                        : 'Comience cargando de forma masiva los registros con la plantilla CSV o agregando una constancia individual.'}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                      onClick={() => setIsBulkModalOpen(true)}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer shadow"
                    >
                      <UploadCloud className="w-4 h-4" />
                      <span>Carga Masiva (CSV / Excel)</span>
                    </button>
                    <button
                      onClick={() => handleOpenNewSingle()}
                      className="px-4 py-2 bg-[#262838] hover:bg-slate-700 text-white rounded-xl text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer border border-[#3e4152]"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Nueva Constancia</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredFolders.map((folder) => (
                    <div
                      key={folder.employeeName}
                      onClick={() => setSelectedFolderEmployee(folder.employeeName)}
                      className={`group bg-[#16171d] rounded-2xl border p-4 space-y-3.5 cursor-pointer transition-all hover:scale-[1.01] hover:shadow-xl ${
                        folder.folderStatus === 'vencido'
                          ? 'border-red-600/40 hover:border-red-500 bg-gradient-to-b from-[#181920] to-red-950/10'
                          : folder.folderStatus === 'por_renovar'
                          ? 'border-amber-600/40 hover:border-amber-500 bg-gradient-to-b from-[#181920] to-amber-950/10'
                          : 'border-[#282a36] hover:border-emerald-500/50'
                      }`}
                    >
                      {/* Top folder title */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base shadow transition-colors ${
                            folder.folderStatus === 'vencido'
                              ? 'bg-red-950/80 text-red-400 border border-red-600/40'
                              : folder.folderStatus === 'por_renovar'
                              ? 'bg-amber-950/80 text-amber-400 border border-amber-600/40'
                              : 'bg-emerald-950/80 text-emerald-400 border border-emerald-600/40 group-hover:bg-emerald-600 group-hover:text-white'
                          }`}>
                            <Folder className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-white text-sm group-hover:text-emerald-300 transition-colors line-clamp-1">
                              {folder.employeeName}
                            </h4>
                            <span className="text-[11px] text-slate-400 block line-clamp-1">
                              {folder.role} ({folder.employeeId})
                            </span>
                          </div>
                        </div>

                        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
                      </div>

                      {/* Stats badge row */}
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-[#222430]">
                        <div className="space-y-0.5">
                          <span className="text-[10px] text-slate-500 block uppercase">Expediente DC-3</span>
                          <strong className="text-white text-xs font-bold">
                            {folder.count} {folder.count === 1 ? 'Constancia' : 'Constancias'} ({folder.totalHours}h)
                          </strong>
                        </div>

                        <div>
                          {folder.folderStatus === 'vigente' && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-950 text-emerald-400 border border-emerald-700">
                              100% Vigente
                            </span>
                          )}
                          {folder.folderStatus === 'por_renovar' && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-950 text-amber-400 border border-amber-700">
                              Vence &lt; 30d
                            </span>
                          )}
                          {folder.folderStatus === 'vencido' && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-950 text-red-400 border border-red-700">
                              Vencido
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Latest Course Preview */}
                      {folder.latestRecord && (
                        <div className="p-2 rounded-lg bg-[#111216] border border-[#222430] text-[11px] text-slate-400 truncate">
                          <span className="text-slate-500 block text-[9px] uppercase">Último Curso Emitido:</span>
                          <span className="text-slate-200 font-medium truncate block">{folder.latestRecord.courseName}</span>
                          <span className="text-[10px] text-slate-400 block font-mono">Emisión: {folder.latestRecord.issueDate}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // VISTA TABLA GENERAL DE FORMATOS DC-3
        <div className="overflow-x-auto rounded-xl border border-[#262836]">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#101115] text-slate-400 font-mono uppercase text-[10px] border-b border-[#262732]">
              <tr>
                <th className="p-3">Folio</th>
                <th className="p-3">Colaborador / Puesto</th>
                <th className="p-3">Curso / Norma STPS</th>
                <th className="p-3">Agente Capacitador</th>
                <th className="p-3">Emisión & Vigencia</th>
                <th className="p-3">Estatus</th>
                <th className="p-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#20212c]">
              {filteredRecords.map((rec) => (
                <tr key={rec.id} className="hover:bg-[#16171d] group">
                  <td className="p-3 font-mono font-bold text-red-400">{rec.id}</td>
                  <td className="p-3 font-medium text-white">
                    <button
                      onClick={() => {
                        setViewMode('folders');
                        setSelectedFolderEmployee(rec.employeeName);
                      }}
                      className="text-left hover:text-emerald-400 transition-colors font-bold block"
                    >
                      {rec.employeeName}
                    </button>
                    <span className="text-[10px] text-slate-400 block">{rec.role} ({rec.employeeId})</span>
                  </td>
                  <td className="p-3 text-slate-300">
                    <div className="font-medium text-white">{rec.courseName}</div>
                    <span className="text-[10px] text-slate-400">{rec.thematicArea || '6000 - Seguridad'}</span>
                  </td>
                  <td className="p-3 text-slate-400">
                    <div>{rec.trainer}</div>
                    <span className="text-[10px] font-mono">{rec.stpsReg}</span>
                  </td>
                  <td className="p-3 font-mono text-slate-300">
                    <div>Emisión: {rec.issueDate}</div>
                    <span className={`text-[10px] font-bold block ${rec.status === 'vencido' ? 'text-red-400' : rec.status === 'por_renovar' ? 'text-amber-400' : 'text-emerald-400'}`}>
                      Vence: {rec.expiryDate} (1 Año)
                    </span>
                  </td>
                  <td className="p-3">
                    {rec.status === 'vigente' && (
                      <span className="bg-emerald-950/80 text-emerald-400 border border-emerald-800 px-2 py-0.5 rounded text-[10px] font-bold">
                        Vigente ({rec.durationHours}h)
                      </span>
                    )}
                    {rec.status === 'por_renovar' && (
                      <span className="bg-amber-950/80 text-amber-400 border border-amber-800 px-2 py-0.5 rounded text-[10px] font-bold">
                        Por Vencer ({rec.daysRemaining}d)
                      </span>
                    )}
                    {rec.status === 'vencido' && (
                      <span className="bg-red-950/80 text-red-400 border border-red-800 px-2 py-0.5 rounded text-[10px] font-bold">
                        Vencido
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleDownloadSingleDC3PDF(rec)}
                        className="p-1 text-slate-400 hover:text-emerald-400 transition-colors"
                        title="Descargar Formato DC-3 oficial en PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleOpenEdit(rec)}
                        className="p-1 text-slate-400 hover:text-white transition-colors"
                        title="Editar constancia"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDeleteDC3(rec.id)}
                        className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                        title="Eliminar registro"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: CARGA MASIVA DE REGISTROS DC-3 (CSV / EXCEL) */}
      {/* ========================================================= */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-3xl w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 border-b border-[#282a36] pb-3">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-sky-400" />
                  Carga Masiva de Formatos DC-3 (STPS)
                </h3>
                <p className="text-xs text-slate-400">
                  Suba un archivo CSV o pegue directamente los registros desde su hoja de cálculo (Excel / Google Sheets).
                </p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="px-3 py-1.5 rounded-lg bg-[#222430] hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center gap-1.5 border border-[#2e3140] cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-emerald-400" />
                <span>Descargar Plantilla</span>
              </button>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-emerald-500 bg-emerald-950/20'
                  : 'border-[#2e3140] bg-[#121317] hover:border-slate-500'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                className="hidden"
                onChange={handleFileUpload}
              />
              <FileSpreadsheet className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-white">Arrastre aquí su archivo CSV o haga clic para seleccionarlo</p>
              <p className="text-[11px] text-slate-400 mt-1">Formato compatible: UTF-8 (.csv, .tsv, .txt delimitado por comas o tabuladores)</p>
            </div>

            {/* Paste Area Alternative */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 block">
                O pegue los datos tabulares directamente desde Excel:
              </label>
              <textarea
                rows={4}
                placeholder="Nombre del Colaborador, CURP, No. Nomina, Puesto, Clave STPS, Nombre Curso, Area Tematica, Capacitador, Reg STPS, Horas, Fecha Emision..."
                value={bulkText}
                onChange={(e) => {
                  setBulkText(e.target.value);
                  parseBulkContent(e.target.value);
                }}
                className="w-full bg-[#101114] border border-[#2a2c38] rounded-xl p-3 text-xs text-slate-200 font-mono focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* Parsing Validation Feedback */}
            {bulkErrors.length > 0 && (
              <div className="p-3 rounded-lg bg-red-950/40 border border-red-500/50 text-xs text-red-300 space-y-1">
                <span className="font-bold block">Advertencias detectadas en la carga:</span>
                {bulkErrors.slice(0, 3).map((err, i) => (
                  <div key={i} className="text-[11px]">• {err}</div>
                ))}
              </div>
            )}

            {/* Preview of Parsed Records */}
            {bulkParsedRecords.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-emerald-400">
                    ✓ Se detectaron {bulkParsedRecords.length} constancias DC-3 válidas listas para importar:
                  </span>
                  <span className="text-[11px] text-slate-400 font-mono">
                    Vigencia asignada: 1 año automático
                  </span>
                </div>

                <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2e3140] overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-[#101115] text-slate-400 sticky top-0 uppercase text-[9px]">
                      <tr>
                        <th className="p-2">Colaborador</th>
                        <th className="p-2">Curso STPS</th>
                        <th className="p-2">Capacitador</th>
                        <th className="p-2">Emisión</th>
                        <th className="p-2">Vigencia (1 Año)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#222430]">
                      {bulkParsedRecords.map((r, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02]">
                          <td className="p-2 font-medium text-white">{r.employeeName}</td>
                          <td className="p-2 text-slate-300">{r.courseName}</td>
                          <td className="p-2 text-slate-400">{r.trainer}</td>
                          <td className="p-2 text-slate-300 font-mono">{r.issueDate}</td>
                          <td className="p-2 text-emerald-400 font-mono font-bold">{r.expiryDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex justify-end gap-2 pt-3 border-t border-[#282a36]">
              <button
                type="button"
                onClick={() => {
                  setIsBulkModalOpen(false);
                  setBulkParsedRecords([]);
                  setBulkText('');
                }}
                className="px-4 py-2 rounded-xl bg-[#20222a] text-slate-300 text-xs font-semibold cursor-pointer hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmBulkUpload}
                disabled={bulkParsedRecords.length === 0}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs cursor-pointer flex items-center gap-1.5 shadow"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Importar {bulkParsedRecords.length} Formatos DC-3</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: REGISTRO / EDICIÓN INDIVIDUAL DC-3 */}
      {/* ========================================================= */}
      {(isSingleModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-2xl w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 border-b border-[#282a36] pb-3">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <FileCheck2 className="w-5 h-5 text-emerald-400" />
                  {isEditModalOpen ? 'Modificar Constancia DC-3 STPS' : 'Registrar Nueva Constancia DC-3'}
                </h3>
                <p className="text-xs text-slate-400">
                  Formato normativo de competencias laborales conforme a la Ley Federal del Trabajo.
                </p>
              </div>
            </div>

            <form onSubmit={handleSaveForm} className="space-y-4 text-xs">
              {/* Sección I: Datos del Trabajador */}
              <div className="p-3.5 rounded-xl bg-[#111216] border border-[#282a36] space-y-3">
                <span className="font-bold text-emerald-400 uppercase text-[10px] tracking-wider block">
                  I. Datos del Trabajador
                </span>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Nombre Completo del Colaborador *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Juan Carlos Mendoza Reyes"
                    value={formRecord.employeeName || ''}
                    onChange={(e) => setFormRecord({ ...formRecord, employeeName: e.target.value })}
                    className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2.5 text-white font-medium focus:outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">CURP</label>
                    <input
                      type="text"
                      placeholder="Ej. MERJ850412HDFNR03"
                      value={formRecord.curp || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, curp: e.target.value.toUpperCase() })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">No. Nómina / ID</label>
                    <input
                      type="text"
                      placeholder="Ej. ISN-OP-101"
                      value={formRecord.employeeId || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, employeeId: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Puesto / Ocupación</label>
                    <input
                      type="text"
                      placeholder="Ej. Operador de Horno"
                      value={formRecord.role || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, role: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>

              {/* Sección II: Datos del Programa */}
              <div className="p-3.5 rounded-xl bg-[#111216] border border-[#282a36] space-y-3">
                <span className="font-bold text-sky-400 uppercase text-[10px] tracking-wider block">
                  II. Datos del Programa de Capacitación
                </span>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Nombre del Curso / Norma Oficial *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. NOM-017-STPS-2008 Equipo de Protección Personal"
                    value={formRecord.courseName || ''}
                    onChange={(e) => setFormRecord({ ...formRecord, courseName: e.target.value })}
                    className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2.5 text-white font-medium focus:outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Área Temática STPS</label>
                    <input
                      type="text"
                      placeholder="Ej. 6000 - Seguridad"
                      value={formRecord.thematicArea || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, thematicArea: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Duración (Horas)</label>
                    <input
                      type="number"
                      min="1"
                      value={formRecord.durationHours || 16}
                      onChange={(e) => setFormRecord({ ...formRecord, durationHours: parseInt(e.target.value, 10) || 16 })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Calificación</label>
                    <input
                      type="text"
                      placeholder="Ej. 100% / Aprobado"
                      value={formRecord.score || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, score: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Agente Capacitador / Instructor</label>
                    <input
                      type="text"
                      placeholder="Ej. Ing. Alondra Tovar"
                      value={formRecord.trainer || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, trainer: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">No. Registro STPS</label>
                    <input
                      type="text"
                      placeholder="Ej. STPS-AUT-2026-ISN09"
                      value={formRecord.stpsReg || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, stpsReg: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">Fecha de Emisión de Constancia *</label>
                    <input
                      type="date"
                      required
                      value={formRecord.issueDate || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const { expiryDate } = calculateValidity(val);
                        setFormRecord({ ...formRecord, issueDate: val, expiryDate });
                      }}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-white font-mono focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="text-slate-300 font-semibold mb-1 block">
                      Fecha de Vencimiento (1 Año Vigencia STPS)
                    </label>
                    <input
                      type="date"
                      value={formRecord.expiryDate || ''}
                      onChange={(e) => setFormRecord({ ...formRecord, expiryDate: e.target.value })}
                      className="w-full bg-[#181920] border border-[#2e3140] rounded-xl p-2 text-emerald-400 font-mono font-bold focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-[#282a36]">
                <button
                  type="button"
                  onClick={() => {
                    setIsSingleModalOpen(false);
                    setIsEditModalOpen(false);
                    setFormRecord({});
                  }}
                  className="px-4 py-2 rounded-xl bg-[#20222a] text-slate-300 text-xs font-semibold hover:bg-slate-700 cursor-pointer"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-[#e52e2e] hover:bg-[#ff3d3d] text-white font-bold text-xs shadow cursor-pointer flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Guardar Constancia DC-3</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CONFIGURACIÓN DE NOTIFICACIONES DC-3 */}
      <DC3NotificationConfigModal
        isOpen={isNotifConfigOpen}
        onClose={() => setIsNotifConfigOpen(false)}
        currentUser={null}
        onSaveSettings={(newSettings) => {
          try {
            localStorage.setItem('snfsic_dc3_notif_config', JSON.stringify(newSettings));
          } catch (e) {
            console.warn(e);
          }
        }}
        dc3Records={dc3Records}
        costCenters={activeCostCenter ? [activeCostCenter] : []}
      />

      {/* MODAL EXPORTADOR EXCEL PROFESIONAL CON FILTROS DE VENCIMIENTO & LOGO */}
      <DC3ExcelExportModal
        isOpen={isExcelExportModalOpen}
        onClose={() => setIsExcelExportModalOpen(false)}
        dc3Records={dc3Records}
        costCenters={activeCostCenter ? [activeCostCenter] : []}
        activeCostCenter={activeCostCenter}
        initialFilterMode={excelFilterPreset}
      />

    </div>
  );
};

export default DC3Manager;
